// @ts-nocheck
import { Button, Form, Input, Modal, Space, Spin, Typography } from "antd";
import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import useHttpClient from "../hooks/useHttpClient";
import useRequestOTP from "../hooks/apis/useRequestOTP";
import { AppContext } from "../constants/contexts";
import { captureException } from "@sentry/react";
import { LoadingOutlined } from "@ant-design/icons";
import { getErrorMessage } from "../utils/http";
import { OtpType } from "../types";
import useWebOtp from "../hooks/useWebOtp";

const OTPModal: FC<{
  open: boolean;
  type: OtpType;
  phoneNumber?: string;
  url?: string;
  method?: "post" | "put" | "delete" | "get";
  payload?: any;
  onClose: () => void;
  onSuccess: () => void;
  onResend: () => void;
  validationRequest?: (otp: string) => void | Promise<void>;
  requestOtp?: boolean;
}> = ({
  open,
  onClose,
  onSuccess,
  onResend,
  type,
  requestOtp,
  validationRequest,
  url,
  method = "post",
  payload,
}) => {
  const httpClient = useHttpClient();
  const requestOTPMutation = useRequestOTP();
  const [otpForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(120); // 2 minutes in seconds
  const { messageApi } = useContext(AppContext);
  const [otpSent, setOtpSent] = useState(false);
  const submittingRef = useRef(false);

  // Reset timer when modal opens
  useEffect(() => {
    if (open) {
      setTimer(120);
    } else {
    }
  }, [open]);

  const handleOTPSubmit = useCallback(async ({ code }: { code: string }) => {
    if (submittingRef.current) return;

    submittingRef.current = true;
    try {
      setLoading(true);
      if (validationRequest) {
        await Promise.resolve(validationRequest(code));
        onSuccess();
        onClose();
        otpForm.resetFields();
        return;
      }
      await httpClient({
        url: url || "/users/me/phone/verify",
        method,
        data: payload || { code },
        headers: { OTP: code },
      });

      if (!url)
        messageApi.open({
          type: "success",
          content: "شماره همراه با موفقیت تایید شد",
        });
      onSuccess();
      onClose();
      otpForm.resetFields();
    } catch (error: any) {
      captureException(error);
      messageApi.open({
        type: "error",
        content: "کد تایید نامعتبر است",
      });
      if (error?.response?.status !== 403) {
        onClose();
        otpForm.resetFields();
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  }, [httpClient, messageApi, method, onClose, onSuccess, otpForm, payload, url, validationRequest]);

  const { start: startWebOtp } = useWebOtp({
    enabled: open,
    onCode: (code) => {
      otpForm.setFieldValue("code", code);
      void handleOTPSubmit({ code });
    },
  });

  const sendOtpRequest = async () => {
    try {
      await requestOTPMutation.trigger({ type });
      setOtpSent(true);
    } catch (error: any) {
      captureException(error);
      const errorData = error?.response?.data;
      // If an active OTP already exists, still show the form
      if (errorData?.error === "An active OTP already exists for this type.") {
        setOtpSent(true);
        messageApi.open({
          type: "info",
          content: getErrorMessage(
            error,
            "کد تایید فعالی وجود دارد. لطفاً از کد قبلی استفاده کنید."
          ),
        });
      } else {
        onClose();
        const errorMessage = getErrorMessage(error, "خطا در ارسال کد تایید");
        messageApi.open({
          type: "error",
          content: errorMessage,
        });
      }
    }
  };

  useEffect(() => {
    if (requestOtp && open) {
      sendOtpRequest();
    }
  }, [requestOtp, open, type, messageApi]);

  // Countdown timer
  useEffect(() => {
    if (timer > 0 && open) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer, open]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleResend = async () => {
    try {
      // Start listening before the request can cause the SMS to arrive.
      startWebOtp();
      await requestOTPMutation.trigger({ type });
      setTimer(120);
      messageApi.open({
        type: "info",
        content: "کد تایید مجدداً ارسال شد",
      });
    } catch (error: any) {
      captureException(error);
      const errorData = error?.response?.data;
      // If an active OTP already exists, inform user but don't treat as error
      if (errorData?.error === "An active OTP already exists for this type.") {
        const errorMessage = getErrorMessage(
          error,
          "کد تایید فعالی وجود دارد. لطفاً از کد قبلی استفاده کنید."
        );
        messageApi.open({
          type: "info",
          content: errorMessage,
        });
      } else {
        const errorMessage = getErrorMessage(error, "خطا در ارسال کد تایید");
        messageApi.open({
          type: "error",
          content: errorMessage,
        });
      }
    }
  };

  return (
    <Modal
      open={open}
      title={
        <div style={{ textAlign: "center", width: "100%" }}>
          <span className="text-xl font-bold">
            {type === "PHONE_VERIFICATION" ? "تایید شماره همراه" : ""}
            {type === "NATIONAL_CODE_VERIFICATION" && "تایید کد ملی"}
            {type === "FINANCIAL_INFO_UPDATE" && "تایید اطلاعات بانکی"}
            {type === "WITHDRAWAL_REQUEST" && "تایید درخواست برداشت"}
          </span>
        </div>
      }
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {(requestOtp && !requestOTPMutation.isMutating && otpSent) || !requestOtp ? (
        <Form
          form={otpForm}
          layout="vertical"
          className="flex flex-col justify-center items-center"
          onFinish={handleOTPSubmit}
          variant='filled'
        >
          <Typography.Text className="text-base text-center">
            کد تایید ۶ رقمی برای شما ارسال شد. <br />
            لطفا برای تایید،  کد را وارد کنید.
          </Typography.Text>
          <Form.Item
            name="code"
            className="mt-6"
            rules={[
              { required: true, message: "لطفا کد تایید را وارد کنید" },
              {
                pattern: /^\d{6}$/,
                message: "کد تایید باید ۶ رقم باشد",
              },
            ]}
          >
            <Input
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              style={{
                direction: "ltr",
                textAlign: "center",
                fontSize: "20px",
                letterSpacing: "8px",
              }}
              placeholder="------"
              autoFocus
              onChange={(event) => {
                const code = event.target.value.replace(/\D/g, "").slice(0, 6);
                otpForm.setFieldValue("code", code);
                if (code.length === 6) void handleOTPSubmit({ code });
              }}
            />
          </Form.Item>

          <Button
            type="link"
            onClick={handleResend}
            disabled={timer > 0 || requestOTPMutation.isMutating}
            loading={requestOTPMutation.isMutating}
            style={{ padding: 0 }}
          >
            {timer > 0 ? `ارسال مجدد کد (${formatTime(timer)})` : "ارسال مجدد کد تایید"}
          </Button>
          <Form.Item>
            <Space className="mt-4" direction="vertical" style={{ width: "100%" }} size="middle">
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  تایید
                </Button>
                <Button onClick={onClose}>لغو</Button>
              </Space>
            </Space>
          </Form.Item>
        </Form>
      ) : (
        <div className="flex flex-col justify-center items-center h-72">
          <Spin indicator={<LoadingOutlined spin />} size="large" />
          <Typography.Text className="text-base text-center mt-4">
            در حال ارسال کد تایید...
          </Typography.Text>
        </div>
      )}
    </Modal>
  );
};

export default OTPModal;
